// WiseSearchOverlay - kompaktowy dropdown wyszukiwarki (Wise UI)
// Zastepuje DesktopOverlay z duzym ProductItem wynikami - kompaktowa lista produktow + kategorie,
// z mozliwoscia szybkiego dodania do koszyka, ulubionych, porownywarki, wyboru jednostki i ilosci.

import React, { RefObject, useLayoutEffect, useMemo, useState } from 'react';
import { ChevronRight, ArrowRight } from 'react-bootstrap-icons';
import classnames from 'classnames';
import { Trans } from 'react-i18next';

import { Loader, Link, Counter } from 'components/controls';
import {
  AddToCartButton,
  FavoriteButton,
  CompareButton
} from 'components/containers';
import WiseSelect from 'components/wise/WiseSelect';
import {
  ICategoryListItem,
  IPaginationResponse,
  IProductListItem,
  IUnit
} from 'api/types';
import productNoImage from 'assets/images/product-no-image.png';

import styles from 'theme/components/wise/WiseSearchOverlay/WiseSearchOverlay.module.scss';

const CATEGORIES_WIDTH = 285;
const GAP_FROM_WRAPPER = 8;

type IResponseCategory = IPaginationResponse<ICategoryListItem>;
type IResponseProduct = IPaginationResponse<IProductListItem>;

type Props = {
  setGlobalKeyword: () => void;
  searchKeyword: string;
  isProductsDataLoading: boolean;
  isCategoriesDataLoading: boolean;
  productsData?: IResponseProduct;
  categoriesData?: IResponseCategory;
  setIsFocus: (state: boolean) => void;
  applyProductsParameters: (categoryId?: number) => void;
  wrapperRef: RefObject<HTMLDivElement>;
};

const highlight = (text: string, phrase: string) => {
  if (!phrase) return text;
  const lower = text.toLowerCase();
  const lowerPhrase = phrase.toLowerCase();
  const idx = lower.indexOf(lowerPhrase);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className={styles.mark}>{text.slice(idx, idx + phrase.length)}</mark>
      {text.slice(idx + phrase.length)}
    </>
  );
};

type ProductRowProps = {
  product: IProductListItem;
  searchKeyword: string;
  onNavigate: () => void;
};

const ProductRow = ({ product, searchKeyword, onNavigate }: ProductRowProps) => {
  const info = product.product_basic_short_info;
  const image = product.product_image_formatted_short_info?.images?.[0];
  const units = product.product_unit_short_info?.units || [];
  const defaultUnitId = product.product_unit_short_info?.default_unit_id || units[0]?.unit_id || 0;

  const [unitId, setUnitId] = useState<number>(defaultUnitId);
  const [quantity, setQuantity] = useState<number>(1);
  const [imageError, setImageError] = useState(false);

  // Fallback do globalnego placeholdera, gdy brak URL-a lub obraz nie laduje sie (404/broken).
  const thumbSrc = !image?.thumb || imageError ? productNoImage : image.thumb;

  const price = useMemo(
    () => product.product_price_short_info?.prices?.find((p) => p.unit_id === unitId),
    [product, unitId]
  );

  const inFavourite =
    product.product_shopping_list_short_info?.in_favourite_shopping_list || false;

  return (
    <div className={styles.productRow}>
      <Link
        to={`/${info?.url_link || ''}`}
        className={styles.productMain}
        onClick={onNavigate}>
        <div className={styles.thumbWrapper}>
          <img
            src={thumbSrc}
            alt={info?.title}
            className={styles.thumb}
            onError={() => setImageError(true)}
          />
        </div>
        <div className={styles.info}>
          <div className={styles.title}>{highlight(info?.title || '', searchKeyword)}</div>
          <div className={styles.meta}>
            {info?.index && (
              <span className={styles.index}>
                <Trans>INDEX</Trans>: {info.index}
              </span>
            )}
            {info?.producer_name && (
              <span className={styles.producer}>{info.producer_name}</span>
            )}
          </div>
        </div>
      </Link>

      <div className={styles.side}>
        {price && (
          <div className={styles.priceWrapper}>
            <div className={styles.priceNet}>
              <strong>{price.price_net_formatted}</strong>
              <span>
                <Trans>netto</Trans>
              </span>
            </div>
            <div className={styles.priceGross}>
              {price.price_gross_formatted} <Trans>brutto</Trans>
            </div>
          </div>
        )}

        <div className={styles.controls} onClick={(e) => e.stopPropagation()}>
          {units.length > 0 && (
            <div className={styles.unitSelect}>
              <WiseSelect<IUnit>
                onChange={(unit) => unit?.unit_id && setUnitId(unit.unit_id)}
                value={unitId}
                options={units.map((unit) => ({
                  value: unit.unit_id,
                  label: `${unit.name}${unit.converter !== 1 ? ` (${unit.converter})` : ''}`,
                  item: unit
                }))}
              />
            </div>
          )}
          <Counter
            value={quantity}
            onChange={(v) => setQuantity(Math.max(1, v))}
            onIncrease={() => setQuantity((q) => q + 1)}
            onDecrease={() => setQuantity((q) => Math.max(1, q - 1))}
            className={styles.counter}
          />
        </div>

        <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
          <FavoriteButton productId={product.id} unitId={unitId} inFavourite={inFavourite} />
          <CompareButton
            productId={product.id}
            title={info?.title || ''}
            image={thumbSrc}
          />
          <AddToCartButton
            productId={product.id}
            unitId={unitId}
            quantity={quantity}
            disabled={!unitId || !info?.is_available}
          />
        </div>
      </div>
    </div>
  );
};

const WiseSearchOverlay = ({
  searchKeyword,
  applyProductsParameters,
  isProductsDataLoading,
  isCategoriesDataLoading,
  productsData,
  categoriesData,
  setIsFocus,
  wrapperRef
}: Props) => {
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  useLayoutEffect(() => {
    const recalc = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const top = Math.round(rect.bottom + GAP_FROM_WRAPPER);
      // Dropdown nie moze siegnac ponizej viewportu - ograniczamy jego wysokosc
      // do dostepnego miejsca pod inputem (z 16px marginesem od dolu).
      const maxHeight = Math.max(240, window.innerHeight - top - 16);
      setPosition({
        top,
        left: Math.round(rect.x),
        width: Math.round(rect.width) + CATEGORIES_WIDTH,
        maxHeight
      });
    };
    recalc();
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [wrapperRef, searchKeyword]);

  if (!searchKeyword || !position) {
    return null;
  }

  const handleNavigate = () => setIsFocus(false);

  // Defensywny filtr - produkty bez product_basic_short_info pomijamy.
  // Nie powinno sie zdarzyc, ale jesli backend zwroci uszkodzony rekord
  // (np. brak prezentacji info dla pewnych SKU), nie psujemy renderu calej listy.
  const validProducts = (productsData?.items || []).filter(
    (product) => !!product.product_basic_short_info
  );

  return (
    <div
      className={classnames(styles.overlay, 'StylePath-Components-Wise-WiseSearchOverlay')}
      onClick={() => setIsFocus(false)}>
      <div
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: position.width,
          maxHeight: `${position.maxHeight}px`
        }}
        className={styles.dropdown}
        onClick={(event) => event.stopPropagation()}>
        <div className={styles.products}>
          <div className={styles.sectionTitle}>
            <Trans>Produkty</Trans>
            {validProducts.length > 0 && (
              <span className={styles.count}>({validProducts.length})</span>
            )}
          </div>
          <div className={styles.list}>
            {isProductsDataLoading && (
              <div className={styles.loaderRow}>
                <Loader />
              </div>
            )}

            {!isProductsDataLoading && productsData && validProducts.length === 0 && (
              <div className={styles.notFound}>
                <h3>
                  <Trans>Brak wyników</Trans>
                </h3>
                <span>
                  <Trans>Zmień kryteria wyszukiwania</Trans>
                </span>
              </div>
            )}

            {validProducts.map((product) => (
              <ProductRow
                key={product.id}
                product={product}
                searchKeyword={searchKeyword}
                onNavigate={handleNavigate}
              />
            ))}
          </div>

          {validProducts.length > 0 && (
            <button className={styles.showAllButton} onClick={() => applyProductsParameters()}>
              <Trans>Zobacz wszystkie produkty</Trans> <ArrowRight />
            </button>
          )}
        </div>

        <div className={styles.categories}>
          <div className={styles.sectionTitle}>
            <Trans>Kategorie</Trans>
          </div>
          <div className={styles.list}>
            {isCategoriesDataLoading && (
              <div className={styles.loaderRow}>
                <Loader />
              </div>
            )}

            {categoriesData?.items?.map((category) => (
              <Link
                to={`/${category.url_link}`}
                key={category.id}
                className={styles.category}
                onClick={handleNavigate}>
                <div className={styles.categoryName}>
                  {highlight(category.name, searchKeyword)}
                  <span className={styles.categoryCount}>({category.products_count})</span>
                </div>
                <ChevronRight className={styles.arrow} />
              </Link>
            ))}

            {!isCategoriesDataLoading && categoriesData && categoriesData.items.length === 0 && (
              <div className={styles.notFoundSmall}>
                <Trans>Brak pasujących kategorii</Trans>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WiseSearchOverlay;
